import { useReportDateState } from "../hooks/useReportDateAccess";
import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaSyncAlt,
  FaPercent,
  FaReceipt,
  FaFileInvoiceDollar,
  FaSearch,
  FaFilter,
  FaTimes,
  FaChevronDown,
  FaChevronUp,
  FaChevronLeft,
  FaChevronRight,
  FaShoppingBag,
} from "react-icons/fa";
import { toLocalYmd } from "../utils/localDate";
import { downloadCSV, downloadExcel, downloadPDF, printThermal, printThermalViaUsb} from "../utils/exportHelpers";
import { formatStoredMoney } from "../utils/currency";
import ReportPageHeader from "../components/ReportPageHeader";
import ReportPeriodPicker from "../components/ReportPeriodPicker";
import ReportControlBar from "../components/ReportControlBar";
import { API_BASE_URL } from "../config/constants";
import { getOfflineTaxReport } from "../offline/financeStore";
import { isFullOfflineModeEnabled } from "../utils/fullOfflineMode";
import {
  isDesktopApp,
  indiaDateKey,
  numberValue,
  extractOrderItems,
  getCachedOrderDetail,
  DESKTOP_CACHE_HEADER,
} from "../utils/offlineReportUtils";

const today = () => toLocalYmd(new Date());

const DEFAULT_SUMMARY = {
  total_orders: 0,
  taxable_orders: 0,
  total_taxable_amount: 0,
  total_cgst: 0,
  total_sgst: 0,
  total_igst: 0,
  total_tax: 0,
  total_gross_amount: 0,
};

const MODAL_ROWS_PER_PAGE = 10;

const TaxReportPage = () => {
  const [userId, setUserId] = useState(null);
  const [fromDate, setFromDate] = useReportDateState(today());
  const [toDate, setToDate] = useReportDateState(today());
  const [paymentMode, setPaymentMode] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  
  const [summary, setSummary] = useState(DEFAULT_SUMMARY);
  const [rateBreakdown, setRateBreakdown] = useState([]);
  const [orders, setOrders] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [, setSourceMessage] = useState("");
  const reportRef = useRef(null);

  // Modal State for Slab Orders Breakdown
  const [selectedSlabModal, setSelectedSlabModal] = useState(null);
  const [modalSearchQuery, setModalSearchQuery] = useState("");
  const [modalPage, setModalPage] = useState(1);
  const [expandedOrders, setExpandedOrders] = useState(new Set());

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("userData") || "{}")?.data;
    if (!user?.id) {
      toast.error("User session expired. Please log in again.");
      setLoading(false);
      return;
    }
    setUserId(user.id);
  }, []);

  useEffect(() => {
    if (userId) {
      fetchReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, fromDate, toDate, paymentMode]);

  const fetchReport = async (opts = {}) => {
    if (!userId) return;
    opts.refresh ? setRefreshing(true) : setLoading(true);
    setSourceMessage("");
    try {
      if (isFullOfflineModeEnabled()) {
        const offlineReport = await getOfflineTaxReport(userId, {
          fromDate,
          toDate,
          paymentMode,
        });
        setSummary({ ...DEFAULT_SUMMARY, ...(offlineReport?.summary || {}) });
        setRateBreakdown(Array.isArray(offlineReport?.rate_breakdown)
          ? offlineReport.rate_breakdown : []);
        setOrders(Array.isArray(offlineReport?.orders) ? offlineReport.orders : []);
        setSourceMessage(offlineSourceMessage(offlineReport));
        return;
      }
      const res = await fetch(`${API_BASE_URL}/report/tax`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          restaurantid: userId,
          fromdate: fromDate,
          todate: toDate,
          payment_mode: paymentMode,
        }),
      });
      const json = await res.json();
      if (!res.ok || !json?.success) {
        const offlineReport = await buildOfflineTaxReport(userId, fromDate, toDate, paymentMode);
        if (!offlineReport) throw new Error(json?.message || "Failed to load tax report");
        setSummary({ ...DEFAULT_SUMMARY, ...(offlineReport.summary || {}) });
        setRateBreakdown(offlineReport.rate_breakdown || []);
        setOrders(offlineReport.orders || []);
        setSourceMessage(offlineSourceMessage(offlineReport));
        return;
      }
      
      if (res.headers.get(DESKTOP_CACHE_HEADER) === "1") {
        const offlineReport = await buildOfflineTaxReport(userId, fromDate, toDate, paymentMode);
        if (offlineReport?.orders?.length || !json?.orders?.length) {
          setSummary({ ...DEFAULT_SUMMARY, ...((offlineReport || json).summary || {}) });
          setRateBreakdown((offlineReport || json).rate_breakdown || []);
          setOrders((offlineReport || json).orders || []);
          setSourceMessage(offlineReport ? offlineSourceMessage(offlineReport) : "Offline mode — showing the last saved tax report for this date range.");
        } else {
          setSummary({ ...DEFAULT_SUMMARY, ...(json.summary || {}) });
          setRateBreakdown(json.rate_breakdown || []);
          setOrders(json.orders || []);
          setSourceMessage("Offline mode — showing the last saved tax report for this date range.");
        }
      } else {
        setSummary({ ...DEFAULT_SUMMARY, ...(json.summary || {}) });
        setRateBreakdown(json.rate_breakdown || []);
        setOrders(json.orders || []);
      }
    } catch (e) {
      try {
        const offlineReport = await buildOfflineTaxReport(userId, fromDate, toDate, paymentMode);
        if (offlineReport) {
          setSummary({ ...DEFAULT_SUMMARY, ...(offlineReport.summary || {}) });
          setRateBreakdown(offlineReport.rate_breakdown || []);
          setOrders(offlineReport.orders || []);
          setSourceMessage(offlineSourceMessage(offlineReport));
        } else {
          toast.error(e.message || "Unable to load tax report");
          setSummary(DEFAULT_SUMMARY);
          setRateBreakdown([]);
          setOrders([]);
        }
      } catch (offlineError) {
        console.warn("Unable to build offline tax report", offlineError);
        toast.error(e.message || "Unable to load tax report");
        setSummary(DEFAULT_SUMMARY);
        setRateBreakdown([]);
        setOrders([]);
      }
    } finally {
      opts.refresh ? setRefreshing(false) : setLoading(false);
    }
  };

  const fmtINR = (n) => formatStoredMoney(n, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const filteredOrders = useMemo(() => {
    if (!searchQuery.trim()) return orders;
    const q = searchQuery.toLowerCase().trim();
    return orders.filter(
      (o) =>
        String(o.billno || "").toLowerCase().includes(q) ||
        String(o.customer_name || "").toLowerCase().includes(q) ||
        String(o.customer_number || "").includes(q) ||
        String(o.payment_mode || "").toLowerCase().includes(q)
    );
  }, [orders, searchQuery]);

  // Orders matching active modal slab
  const slabOrders = useMemo(() => {
    if (!selectedSlabModal) return [];
    const targetLabel = String(selectedSlabModal.rate_label || "").trim().toLowerCase();
    const targetKey = String(selectedSlabModal.rate ?? "").trim();

    return orders.filter((o) => {
      const oLabel = String(o.rate_label || "").trim().toLowerCase();
      const oKey = String(o.rate_key ?? "").trim();
      return oLabel === targetLabel || oKey === targetKey;
    });
  }, [orders, selectedSlabModal]);

  const filteredSlabOrders = useMemo(() => {
    if (!modalSearchQuery.trim()) return slabOrders;
    const q = modalSearchQuery.toLowerCase().trim();
    return slabOrders.filter(
      (o) =>
        String(o.billno || "").toLowerCase().includes(q) ||
        String(o.customer_name || "").toLowerCase().includes(q) ||
        String(o.customer_number || "").includes(q) ||
        String(o.payment_mode || "").toLowerCase().includes(q)
    );
  }, [slabOrders, modalSearchQuery]);

  const modalTotalPages = useMemo(() => {
    return Math.ceil(filteredSlabOrders.length / MODAL_ROWS_PER_PAGE) || 1;
  }, [filteredSlabOrders]);

  const modalPagedOrders = useMemo(() => {
    const start = (modalPage - 1) * MODAL_ROWS_PER_PAGE;
    return filteredSlabOrders.slice(start, start + MODAL_ROWS_PER_PAGE);
  }, [filteredSlabOrders, modalPage]);

  const openSlabModal = (slab) => {
    setSelectedSlabModal(slab);
    setModalSearchQuery("");
    setModalPage(1);
  };

  const closeSlabModal = () => {
    setSelectedSlabModal(null);
    setModalSearchQuery("");
    setModalPage(1);
  };

  const toggleExpandOrder = (id) => {
    setExpandedOrders((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const exportCsv = () => {
    const rateRows = rateBreakdown.map((r, idx) => [
      `Rate Slab ${idx + 1}`,
      r.rate_label,
      r.order_count,
      r.taxable_amount,
      r.cgst,
      r.sgst,
      r.igst,
      r.total_tax,
    ]);
    
    const orderRows = filteredOrders.map((o) => [
      o.billno,
      new Date(o.created_at).toLocaleString("en-IN"),
      o.order_type,
      o.payment_mode,
      o.customer_name || "N/A",
      o.taxable_amount,
      o.cgst,
      o.sgst,
      o.igst,
      o.total_tax,
      o.grand_total,
    ]);

    const exportRows = [
      ["--- TAX RATE SLABS ---"],
      ["#", "Tax Rate", "Orders", "Taxable Value", "CGST", "SGST", "IGST", "Total Tax"],
      ...rateRows,
      [""],
      ["--- BILL WISE TAX DETAILS ---"],
      ["Bill No", "Date & Time", "Type", "Payment Mode", "Customer", "Taxable Amount", "CGST", "SGST", "IGST", "Total Tax", "Grand Total"],
      ...orderRows,
    ];

    downloadCSV(
      `tax_report_${fromDate}_to_${toDate}.csv`,
      ["Tax Report Summary Export"],
      exportRows
    );
  };

  const exportExcel = () => {
    const rateRows = rateBreakdown.map((r, idx) => [
      idx + 1,
      r.rate_label,
      r.order_count,
      r.taxable_amount,
      r.cgst,
      r.sgst,
      r.igst,
      r.total_tax,
    ]);

    downloadExcel(
      `tax_report_${fromDate}_to_${toDate}.xlsx`,
      ["#", "Tax Slab", "Orders", "Taxable Amount", "CGST", "SGST", "IGST", "Total Tax"],
      rateRows
    );
  };

  const exportPdf = () => downloadPDF(reportRef.current, `tax_report_${fromDate}_to_${toDate}.pdf`);

  return (
    <Page>
      <ToastContainer position="top-right" autoClose={2200} />

      <ReportPageHeader
        title="Tax Report (GST Summary)"
        subtitle="Detailed GST breakdown by tax rate slabs, net taxable value, CGST, SGST, and order-wise tax."
        stats={[
          { label: "Tax Collected", value: fmtINR(summary.total_tax) },
          { label: "Net Taxable", value: fmtINR(summary.total_taxable_amount) },
          { label: "CGST", value: fmtINR(summary.total_cgst) },
          { label: "SGST", value: fmtINR(summary.total_sgst) },
          { label: "Gross Sales", value: fmtINR(summary.total_gross_amount) },
          { label: "Taxable Bills", value: summary.taxable_orders },
        ]}
      />

      <ReportControlBar>
        <ReportPeriodPicker
          fromDate={fromDate}
          toDate={toDate}
          onChange={({ from, to, fromDate: f, toDate: t }) => {
            const nextFrom = from || f;
            const nextTo = to || t;
            if (nextFrom) setFromDate(nextFrom);
            if (nextTo) setToDate(nextTo);
          }}
        />

        <FilterGroup>
          <FilterIcon><FaFilter /></FilterIcon>
          <Select
            value={paymentMode}
            onChange={(e) => setPaymentMode(e.target.value)}
          >
            <option value="all">All Payment Modes</option>
            <option value="cash">Cash</option>
            <option value="card">Card</option>
            <option value="upi">UPI</option>
            <option value="credit">Credit</option>
          </Select>
        </FilterGroup>

        <SearchBox>
          <FaSearch style={{ color: "#888", marginRight: 6 }} />
          <SearchInput
            type="text"
            placeholder="Search bill no or customer..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </SearchBox>

        <PrimaryBtn onClick={() => fetchReport({ refresh: true })}>
          <FaSyncAlt /> {refreshing ? "Refreshing..." : "Refresh"}
        </PrimaryBtn>
      </ReportControlBar>


      <Container ref={reportRef}>
        {loading ? (
          <Center>Loading Tax Report Data…</Center>
        ) : (
          <>
            {/* Top Cards Grid */}
            <MetricsGrid>
              <MetricCard color="#0f766e" bg="#ccfbf1">
                <CardIcon color="#0f766e"><FaFileInvoiceDollar /></CardIcon>
                <div>
                  <CardLabel>Total Tax Collected</CardLabel>
                  <CardValue>{fmtINR(summary.total_tax)}</CardValue>
                  <CardSubtext>{summary.taxable_orders} Taxable Orders</CardSubtext>
                </div>
              </MetricCard>

              <MetricCard color="var(--app-primary)" bg="var(--app-primary-soft)">
                <CardIcon color="var(--app-primary)"><FaPercent /></CardIcon>
                <div>
                  <CardLabel>Net Taxable Value</CardLabel>
                  <CardValue>{fmtINR(summary.total_taxable_amount)}</CardValue>
                  <CardSubtext>Excluding Tax</CardSubtext>
                </div>
              </MetricCard>

              <MetricCard color="#b45309" bg="#fef3c7">
                <CardIcon color="#b45309"><FaReceipt /></CardIcon>
                <div>
                  <CardLabel>CGST + SGST Breakdown</CardLabel>
                  <CardValue>
                    <span style={{ fontSize: "0.95rem" }}>C: {fmtINR(summary.total_cgst)}</span> |{" "}
                    <span style={{ fontSize: "0.95rem" }}>S: {fmtINR(summary.total_sgst)}</span>
                  </CardValue>
                  <CardSubtext>Central & State GST</CardSubtext>
                </div>
              </MetricCard>

              <MetricCard color="#4338ca" bg="#e0e7ff">
                <CardIcon color="#4338ca"><FaFileInvoiceDollar /></CardIcon>
                <div>
                  <CardLabel>Gross Sales (Total)</CardLabel>
                  <CardValue>{fmtINR(summary.total_gross_amount)}</CardValue>
                  <CardSubtext>{summary.total_orders} Total Orders</CardSubtext>
                </div>
              </MetricCard>
            </MetricsGrid>

            {/* Tax Rate Slabs Section */}
            <SectionCard>
              <SectionHeader>
                <h3>Tax Rate Slab Breakdown</h3>
                <SectionSub>Click on the Orders count to view order & product details in a modal</SectionSub>
              </SectionHeader>
              {rateBreakdown.length === 0 ? (
                <EmptyMessage>No tax slab breakdown available for the selected period.</EmptyMessage>
              ) : (
                <TableWrap>
                  <Table>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Tax Rate Slab</th>
                        <th style={{ textAlign: "center" }}>Orders (Click to View)</th>
                        <th style={{ textAlign: "right" }}>Taxable Amount</th>
                        <th style={{ textAlign: "right" }}>CGST</th>
                        <th style={{ textAlign: "right" }}>SGST</th>
                        <th style={{ textAlign: "right" }}>IGST</th>
                        <th style={{ textAlign: "right" }}>Total Tax</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rateBreakdown.map((r, idx) => (
                        <tr key={idx}>
                          <td>{idx + 1}</td>
                          <td>
                            <RateBadge>{r.rate_label}</RateBadge>
                          </td>
                          <td style={{ textAlign: "center" }}>
                            <OrderCountButton
                              type="button"
                              onClick={() => openSlabModal(r)}
                              title={`View all ${r.order_count} orders for ${r.rate_label}`}
                            >
                              <span>{r.order_count} Orders</span>
                            </OrderCountButton>
                          </td>
                          <td style={{ textAlign: "right" }}>{fmtINR(r.taxable_amount)}</td>
                          <td style={{ textAlign: "right" }}>{fmtINR(r.cgst)}</td>
                          <td style={{ textAlign: "right" }}>{fmtINR(r.sgst)}</td>
                          <td style={{ textAlign: "right" }}>{fmtINR(r.igst)}</td>
                          <td style={{ textAlign: "right", fontWeight: "bold", color: "#0f766e" }}>
                            {fmtINR(r.total_tax)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </TableWrap>
              )}
            </SectionCard>

            {/* Detailed Bill List Section */}
            <SectionCard>
              <SectionHeader>
                <h3>Bill-wise Tax Details</h3>
                <SectionSub>Individual order tax calculations and totals</SectionSub>
              </SectionHeader>

              {filteredOrders.length === 0 ? (
                <EmptyMessage>No orders found for the selected date range and filters.</EmptyMessage>
              ) : (
                <TableWrap>
                  <Table>
                    <thead>
                      <tr>
                        <th>Bill No</th>
                        <th>Date & Time</th>
                        <th>Type</th>
                        <th>Payment Mode</th>
                        <th>Customer</th>
                        <th style={{ textAlign: "right" }}>Taxable Amount</th>
                        <th style={{ textAlign: "right" }}>CGST</th>
                        <th style={{ textAlign: "right" }}>SGST</th>
                        <th style={{ textAlign: "right" }}>Total Tax</th>
                        <th style={{ textAlign: "right" }}>Grand Total</th>
                        <th style={{ textAlign: "center" }}>Items</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredOrders.map((o) => {
                        const isExpanded = expandedOrders.has(o.id);
                        return (
                          <React.Fragment key={o.id}>
                            <tr>
                              <td><strong>{o.billno}</strong></td>
                              <td style={{ fontSize: "0.85rem", color: "#475569" }}>
                                {new Date(o.created_at).toLocaleString("en-IN", {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </td>
                              <td><TypeBadge>{o.order_type}</TypeBadge></td>
                              <td><ModeBadge>{o.payment_mode}</ModeBadge></td>
                              <td>
                                {o.customer_name ? (
                                  <div>
                                    <div style={{ fontWeight: 600 }}>{o.customer_name}</div>
                                    {o.customer_number && (
                                      <small style={{ color: "#64748b" }}>{o.customer_number}</small>
                                    )}
                                  </div>
                                ) : (
                                  <span style={{ color: "#94a3b8" }}>—</span>
                                )}
                              </td>
                              <td style={{ textAlign: "right" }}>{fmtINR(o.taxable_amount)}</td>
                              <td style={{ textAlign: "right", color: "#475569" }}>{fmtINR(o.cgst)}</td>
                              <td style={{ textAlign: "right", color: "#475569" }}>{fmtINR(o.sgst)}</td>
                              <td style={{ textAlign: "right", fontWeight: "bold", color: "#0f766e" }}>
                                {fmtINR(o.total_tax)}
                              </td>
                              <td style={{ textAlign: "right", fontWeight: "bold", color: "#1e293b" }}>
                                {fmtINR(o.grand_total)}
                              </td>
                              <td style={{ textAlign: "center" }}>
                                <ExpandBtn
                                  type="button"
                                  onClick={() => toggleExpandOrder(o.id)}
                                >
                                  <FaShoppingBag style={{ fontSize: "0.75rem" }} />
                                  <span>{o.items?.length || 0}</span>
                                  {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                                </ExpandBtn>
                              </td>
                            </tr>
                            {isExpanded && (
                              <ExpandedRow>
                                <td colSpan={11}>
                                  <NestedProductsWrap>
                                    <NestedTitle>
                                      <FaShoppingBag /> Products in Order {o.billno}
                                    </NestedTitle>

                                    {!o.items || o.items.length === 0 ? (
                                      <div style={{ color: "#94a3b8", fontSize: "0.85rem", padding: "8px 0" }}>
                                        No item details recorded.
                                      </div>
                                    ) : (
                                      <NestedTable>
                                        <thead>
                                          <tr>
                                            <th>Product / Item Name</th>
                                            <th style={{ textAlign: "center" }}>Quantity</th>
                                            <th style={{ textAlign: "right" }}>Unit Price</th>
                                            <th style={{ textAlign: "right" }}>Total Amount</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {o.items.map((item, iIdx) => (
                                            <tr key={iIdx}>
                                              <td><strong>{item.item_name}</strong></td>
                                              <td style={{ textAlign: "center" }}>{item.qty}</td>
                                              <td style={{ textAlign: "right" }}>{fmtINR(item.unit_price)}</td>
                                              <td style={{ textAlign: "right", fontWeight: "600" }}>
                                                {fmtINR(item.total_price)}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </NestedTable>
                                    )}
                                  </NestedProductsWrap>
                                </td>
                              </ExpandedRow>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </Table>
                </TableWrap>
              )}
            </SectionCard>

            {/* Action Bar */}
            <ActionsRow>
              <PrimaryBtn onClick={exportExcel}>Export Excel</PrimaryBtn>
              <PrimaryBtn onClick={exportPdf}>Export PDF</PrimaryBtn>
              <PrimaryBtn onClick={() => printThermal(reportRef.current, `tax_report_${fromDate}_to_${toDate}`)}>
                Thermal Print
              </PrimaryBtn><PrimaryBtn onClick={() => printThermalViaUsb(reportRef.current, `tax_report_${fromDate}_to_${toDate}`)}>
                Thermal Print via USB
              </PrimaryBtn>
              <PrimaryBtn onClick={exportCsv}>CSV</PrimaryBtn>
            </ActionsRow>
          </>
        )}
      </Container>

      {/* Modal Popup for Orders in Selected Tax Slab */}
      {selectedSlabModal && (
        <ModalOverlay onClick={closeSlabModal}>
          <ModalContainer onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <div>
                <ModalTitle>
                  Orders in Tax Slab <RateBadge style={{ fontSize: "0.9rem" }}>{selectedSlabModal.rate_label}</RateBadge>
                </ModalTitle>
                <ModalSub>
                  Showing {filteredSlabOrders.length} orders matching {selectedSlabModal.rate_label}
                </ModalSub>
              </div>
              <CloseBtn onClick={closeSlabModal}>
                <FaTimes />
              </CloseBtn>
            </ModalHeader>

            <ModalFilterBar>
              <SearchBox style={{ maxWidth: "100%" }}>
                <FaSearch style={{ color: "#888", marginRight: 6 }} />
                <SearchInput
                  type="text"
                  placeholder="Search bill no, customer, payment mode..."
                  value={modalSearchQuery}
                  onChange={(e) => {
                    setModalSearchQuery(e.target.value);
                    setModalPage(1);
                  }}
                />
              </SearchBox>
            </ModalFilterBar>

            <ModalBody>
              {filteredSlabOrders.length === 0 ? (
                <EmptyMessage>No orders found in this slab for the current filters.</EmptyMessage>
              ) : (
                <TableWrap>
                  <Table>
                    <thead>
                      <tr>
                        <th>Bill No</th>
                        <th>Date & Time</th>
                        <th>Mode</th>
                        <th>Customer</th>
                        <th style={{ textAlign: "right" }}>Taxable</th>
                        <th style={{ textAlign: "right" }}>Tax</th>
                        <th style={{ textAlign: "right" }}>Grand Total</th>
                        <th style={{ textAlign: "center" }}>Products</th>
                      </tr>
                    </thead>
                    <tbody>
                      {modalPagedOrders.map((o) => {
                        const isExpanded = expandedOrders.has(`modal-${o.id}`);
                        return (
                          <React.Fragment key={o.id}>
                            <tr>
                              <td><strong>{o.billno}</strong></td>
                              <td style={{ fontSize: "0.82rem", color: "#64748b" }}>
                                {new Date(o.created_at).toLocaleString("en-IN", {
                                  day: "2-digit",
                                  month: "short",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </td>
                              <td><ModeBadge>{o.payment_mode}</ModeBadge></td>
                              <td>{o.customer_name || <span style={{ color: "#94a3b8" }}>—</span>}</td>
                              <td style={{ textAlign: "right" }}>{fmtINR(o.taxable_amount)}</td>
                              <td style={{ textAlign: "right", fontWeight: "bold", color: "#0f766e" }}>
                                {fmtINR(o.total_tax)}
                              </td>
                              <td style={{ textAlign: "right", fontWeight: "bold" }}>{fmtINR(o.grand_total)}</td>
                              <td style={{ textAlign: "center" }}>
                                <ExpandBtn
                                  type="button"
                                  onClick={() => toggleExpandOrder(`modal-${o.id}`)}
                                >
                                  <FaShoppingBag style={{ fontSize: "0.75rem" }} />
                                  <span>{o.items?.length || 0} Items</span>
                                  {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                                </ExpandBtn>
                              </td>
                            </tr>
                            {isExpanded && (
                              <ExpandedRow>
                                <td colSpan={8}>
                                  <NestedProductsWrap>
                                    <NestedTitle>
                                      <FaShoppingBag /> Products in Order {o.billno}
                                    </NestedTitle>
                                    {!o.items || o.items.length === 0 ? (
                                      <div style={{ color: "#94a3b8", fontSize: "0.85rem", padding: "8px 0" }}>
                                        No product details recorded.
                                      </div>
                                    ) : (
                                      <NestedTable>
                                        <thead>
                                          <tr>
                                            <th>Product / Item Name</th>
                                            <th style={{ textAlign: "center" }}>Qty</th>
                                            <th style={{ textAlign: "right" }}>Unit Price</th>
                                            <th style={{ textAlign: "right" }}>Total Amount</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {o.items.map((item, iIdx) => (
                                            <tr key={iIdx}>
                                              <td><strong>{item.item_name}</strong></td>
                                              <td style={{ textAlign: "center" }}>{item.qty}</td>
                                              <td style={{ textAlign: "right" }}>{fmtINR(item.unit_price)}</td>
                                              <td style={{ textAlign: "right", fontWeight: "600" }}>
                                                {fmtINR(item.total_price)}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </NestedTable>
                                    )}
                                  </NestedProductsWrap>
                                </td>
                              </ExpandedRow>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </Table>
                </TableWrap>
              )}
            </ModalBody>

            {/* Modal Pagination Footer */}
            {filteredSlabOrders.length > 0 && (
              <ModalFooter>
                <PaginationInfo>
                  Showing {Math.min((modalPage - 1) * MODAL_ROWS_PER_PAGE + 1, filteredSlabOrders.length)} to{" "}
                  {Math.min(modalPage * MODAL_ROWS_PER_PAGE, filteredSlabOrders.length)} of {filteredSlabOrders.length} orders
                </PaginationInfo>

                <PaginationControls>
                  <PageBtn
                    type="button"
                    disabled={modalPage === 1}
                    onClick={() => setModalPage((prev) => Math.max(1, prev - 1))}
                  >
                    <FaChevronLeft /> Prev
                  </PageBtn>

                  <PageNumbers>
                    {Array.from({ length: modalTotalPages }, (_, i) => i + 1)
                      .filter((p) => p === 1 || p === modalTotalPages || Math.abs(p - modalPage) <= 1)
                      .map((p, idx, arr) => (
                        <React.Fragment key={p}>
                          {idx > 0 && arr[idx - 1] !== p - 1 && <span style={{ color: "#94a3b8" }}>…</span>}
                          <NumBtn
                            type="button"
                            $active={p === modalPage}
                            onClick={() => setModalPage(p)}
                          >
                            {p}
                          </NumBtn>
                        </React.Fragment>
                      ))}
                  </PageNumbers>

                  <PageBtn
                    type="button"
                    disabled={modalPage >= modalTotalPages}
                    onClick={() => setModalPage((prev) => Math.min(modalTotalPages, prev + 1))}
                  >
                    Next <FaChevronRight />
                  </PageBtn>
                </PaginationControls>
              </ModalFooter>
            )}
          </ModalContainer>
        </ModalOverlay>
      )}
    </Page>
  );
};

export default TaxReportPage;

/* ====== Styled Components ====== */
const Page = styled.div`
  background: radial-gradient(circle at 20% 20%, #f5f8ff, #eef2ff 55%, #f5f7fb 100%);
  min-height: 100vh;
  padding: 18px;
`;


const normalizeGstSlabRate = (calculatedRate) => {
  if (calculatedRate <= 0.5) return 0.0;
  if (calculatedRate > 0.5 && calculatedRate < 4.0) return 3.0;
  if (calculatedRate >= 4.0 && calculatedRate < 8.5) return 5.0;
  if (calculatedRate >= 8.5 && calculatedRate < 15.0) return 12.0;
  if (calculatedRate >= 15.0 && calculatedRate < 23.0) return 18.0;
  return 28.0;
};

const getPaymentMode = (o) => {
  const raw = String(o?.mode ?? o?.payment_mode ?? o?.paymentmode ?? o?.paymenttype ?? o?.payment_type ?? "cash").toLowerCase();
  if (raw.includes("cash")) return "cash";
  if (raw.includes("card")) return "card";
  if (raw.includes("upi") || raw.includes("online") || raw.includes("gpay") || raw.includes("phonepe") || raw.includes("paytm")) return "upi";
  if (raw.includes("credit") || raw.includes("udhar") || raw.includes("khata") || raw.includes("pending")) return "credit";
  return raw || "cash";
};

const buildOfflineTaxReport = async (restaurantId, fromDate, toDate, paymentMode) => {
  if (!isDesktopApp()) return null;
  const [cachedOrders, pendingRows] = await Promise.all([
    window.desktopOrders.list({ restaurantId }),
    window.desktopOrderQueue?.list({ restaurantId, includeDeferred: true }) || [],
  ]);

  const orders = (cachedOrders || []).filter((order) => {
    const d = indiaDateKey(order?.created_at ?? order?.createdAt ?? order?.date);
    return d >= fromDate && d <= toDate;
  });
  const pendingOrders = (pendingRows || [])
    .filter((entry) => {
      const d = indiaDateKey(entry?.createdAt);
      return d >= fromDate && d <= toDate;
    })
    .map((entry) => ({
      ...entry.payload,
      id: `pending-${entry.id}`,
      created_at: entry.createdAt,
      is_pending_sync: true,
    }));

  let allOrders = [...pendingOrders, ...orders].filter(o => !o.is_deleted && !o.is_cancel);

  if (paymentMode && paymentMode !== "all") {
    allOrders = allOrders.filter(o => getPaymentMode(o) === paymentMode);
  }

  const rateSlabs = {};
  const orderList = [];

  let grandTaxableAmount = 0.0;
  let grandCgst = 0.0;
  let grandSgst = 0.0;
  let grandIgst = 0.0;
  let grandTotalTax = 0.0;
  let grandGrossAmount = 0.0;
  let taxableOrdersCount = 0;

  await Promise.all(allOrders.map(async (order) => {
    let orderTotalTax = numberValue(order.tax_total ?? order.totaltax ?? order.tax ?? order.total_tax);
    
    if (orderTotalTax === 0) {
      const detail = order?.is_pending_sync ? null : await getCachedOrderDetail(restaurantId, order);
      
      if (detail) {
        if (detail.tax_details?.tax_total !== undefined) {
          orderTotalTax = numberValue(detail.tax_details.tax_total);
        } else if (Array.isArray(detail.gstbreakup) && detail.gstbreakup.length > 0) {
          orderTotalTax = detail.gstbreakup.reduce((sum, b) => sum + numberValue(b.total ?? (numberValue(b.cgst) + numberValue(b.sgst))), 0);
        } else if (detail.orderdetails?.tax_total !== undefined || detail.orderdetails?.totaltax !== undefined) {
          orderTotalTax = numberValue(detail.orderdetails.tax_total ?? detail.orderdetails.totaltax);
        }
      }

      if (orderTotalTax === 0) {
        const items = extractOrderItems(detail, order);
        orderTotalTax = items.reduce((sum, item) => {
          const qty = numberValue(item?.quantity ?? item?.qty ?? 0);
          const explicitTax = numberValue(item?.taxamount ?? item?.tax ?? 0);
          const unitBase = numberValue(item?.priceaftertax ?? item?.price ?? 0);
          const unitWithTax = numberValue(item?.taxedprice ?? item?.priceaftertax ?? item?.price ?? 0);
          const unitTax = explicitTax > 0 ? explicitTax : Math.max(0, unitWithTax - unitBase);
          return sum + (unitTax * qty);
        }, 0);
      }
    }

    const orderGrandTotal = numberValue(order?.grand_total ?? order?.grandtotal ?? order?.totalamount ?? order?.amount ?? order?.total);
    const orderCgst = Number((orderTotalTax / 2).toFixed(2));
    const orderSgst = Number((orderTotalTax - orderCgst).toFixed(2));
    const orderTaxable = Math.max(0.0, orderGrandTotal - orderTotalTax);

    const rawRate = orderTaxable > 0 ? (orderTotalTax / orderTaxable) * 100 : 0.0;
    const rate = normalizeGstSlabRate(rawRate);
    const rateKey = rate.toFixed(1);

    if (!rateSlabs[rateKey]) {
      rateSlabs[rateKey] = {
        rate,
        rate_label: rate > 0 ? `${rate}% GST` : '0% (Exempt)',
        taxable_amount: 0,
        cgst: 0,
        sgst: 0,
        igst: 0,
        total_tax: 0,
        order_count: 0,
      };
    }

    rateSlabs[rateKey].taxable_amount += orderTaxable;
    rateSlabs[rateKey].cgst += orderCgst;
    rateSlabs[rateKey].sgst += orderSgst;
    rateSlabs[rateKey].total_tax += orderTotalTax;
    rateSlabs[rateKey].order_count += 1;

    grandTaxableAmount += orderTaxable;
    grandCgst += orderCgst;
    grandSgst += orderSgst;
    grandTotalTax += orderTotalTax;
    grandGrossAmount += orderGrandTotal;

    if (orderTotalTax > 0) {
      taxableOrdersCount += 1;
    }

    orderList.push({
      id: order.id,
      billno: order.ordercode ?? order.billno ?? order.id,
      created_at: order.created_at ?? order.createdAt,
      order_type: order.ordertype ?? order.order_type ?? "Dine In",
      payment_mode: getPaymentMode(order),
      customer_name: order.customername ?? order.customer_name ?? order.partyname ?? "",
      customer_number: order.customernumber ?? order.customer_number ?? "",
      taxable_amount: orderTaxable,
      cgst: orderCgst,
      sgst: orderSgst,
      igst: 0,
      total_tax: orderTotalTax,
      grand_total: orderGrandTotal,
      rate_key: rateKey,
      rate_label: rateSlabs[rateKey].rate_label,
    });
  }));

  const rateBreakdown = Object.values(rateSlabs).sort((a, b) => a.rate - b.rate);

  return {
    success: true,
    offline: true,
    summary: {
      total_orders: allOrders.length,
      taxable_orders: taxableOrdersCount,
      total_taxable_amount: grandTaxableAmount,
      total_cgst: grandCgst,
      total_sgst: grandSgst,
      total_igst: grandIgst,
      total_tax: grandTotalTax,
      total_gross_amount: grandGrossAmount,
    },
    rate_breakdown: rateBreakdown,
    orders: orderList.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
  };
};

const offlineSourceMessage = (report = {}) => {
  const missing = numberValue(report?.missing_detail_count);
  if (report?.full_offline && missing > 0) {
    return `Full Offline — tax totals are local; ${missing} saved bill${missing === 1 ? " has" : "s have"} no product detail.`;
  }
  if (report?.full_offline) {
    return "Full Offline — GST summary is calculated only from the isolated local database.";
  }
  return "Offline mode — tax summary calculated from range-sync SQLite bills and pending orders.";
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Center = styled.div`
  padding: 60px;
  text-align: center;
  font-size: 1.1rem;
  color: #64748b;
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #ffffff;
  padding: 6px 12px;
  border-radius: 10px;
  border: 1px solid #cbd5e1;
`;

const FilterIcon = styled.div`
  color: #64748b;
  display: flex;
  align-items: center;
`;

const Select = styled.select`
  border: none;
  background: transparent;
  font-size: 0.9rem;
  font-weight: 500;
  color: #334155;
  outline: none;
  cursor: pointer;
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  padding: 6px 12px;
  border-radius: 10px;
  flex: 1;
  max-width: 300px;
`;

const SearchInput = styled.input`
  border: none;
  outline: none;
  width: 100%;
  font-size: 0.88rem;
  color: #1e293b;
`;

const PrimaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: var(--app-primary);
  color: #ffffff;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.88rem;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover {
    background: #054878;
    box-shadow: 0 4px 12px rgba(7, 95, 158, 0.2);
  }
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 16px;
`;

const MetricCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 18px 20px;
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.03);
`;

const CardIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: ${(props) => props.bg || "#f1f5f9"};
  color: ${(props) => props.color || "var(--app-primary)"};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
`;

const CardLabel = styled.div`
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
`;

const CardValue = styled.div`
  font-size: 1.25rem;
  font-weight: 800;
  color: #0f172a;
  margin-top: 3px;
`;

const CardSubtext = styled.div`
  font-size: 0.75rem;
  color: #94a3b8;
  margin-top: 2px;
`;

const SectionCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 20px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.03);
`;

const SectionHeader = styled.div`
  margin-bottom: 16px;
  h3 {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 700;
    color: #0f172a;
  }
`;

const SectionSub = styled.p`
  margin: 4px 0 0 0;
  font-size: 0.82rem;
  color: #64748b;
`;

const EmptyMessage = styled.div`
  padding: 30px;
  text-align: center;
  color: #94a3b8;
  font-size: 0.92rem;
`;

const TableWrap = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.88rem;

  th {
    background: #f8fafc;
    color: #475569;
    font-weight: 700;
    text-transform: uppercase;
    font-size: 0.72rem;
    letter-spacing: 0.05em;
    padding: 12px 14px;
    border-bottom: 2px solid #e2e8f0;
    text-align: left;
  }

  td {
    padding: 12px 14px;
    border-bottom: 1px solid #f1f5f9;
    color: #1e293b;
  }

  tbody tr:hover {
    background: #f8fafc;
  }
`;

const RateBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 20px;
  background: var(--app-primary-soft);
  color: var(--app-primary);
  font-weight: 700;
  font-size: 0.78rem;
`;

const TypeBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  background: #f1f5f9;
  color: #475569;
  font-size: 0.75rem;
  font-weight: 600;
`;

const ModeBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  background: #ecfdf5;
  color: #047857;
  font-size: 0.75rem;
  font-weight: 600;
`;

const OrderCountButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 20px;
  background: var(--app-primary);
  color: #ffffff;
  border: none;
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 6px rgba(7, 95, 158, 0.15);

  &:hover {
    background: #054878;
    transform: translateY(-1px);
    box-shadow: 0 4px 10px rgba(7, 95, 158, 0.3);
  }
`;

const ExpandBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  background: #f1f5f9;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.78rem;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #e2e8f0;
    color: #0f172a;
  }
`;

const ExpandedRow = styled.tr`
  background: #f8fafc;
  td {
    padding: 12px 18px !important;
  }
`;

const NestedProductsWrap = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 14px;
`;

const NestedTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--app-primary);
  margin-bottom: 10px;
`;

const NestedTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;

  th {
    background: #f1f5f9;
    color: #475569;
    padding: 8px 10px;
    font-size: 0.7rem;
    text-transform: uppercase;
  }

  td {
    padding: 8px 10px;
    border-bottom: 1px solid #f1f5f9;
  }
`;

const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 10px;
`;

/* ====== Modal Styled Components ====== */
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(4px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const ModalContainer = styled.div`
  background: #ffffff;
  border-radius: 20px;
  width: 100%;
  max-width: 950px;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  animation: fadeIn 0.2s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: scale(0.96);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 1.15rem;
  font-weight: 800;
  color: #0f172a;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ModalSub = styled.p`
  margin: 3px 0 0 0;
  font-size: 0.8rem;
  color: #64748b;
`;

const CloseBtn = styled.button`
  background: #f1f5f9;
  border: none;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  color: #64748b;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #e2e8f0;
    color: #0f172a;
  }
`;

const ModalFilterBar = styled.div`
  padding: 14px 24px;
  background: #ffffff;
  border-bottom: 1px solid #f1f5f9;
`;

const ModalBody = styled.div`
  padding: 16px 24px;
  overflow-y: auto;
  flex: 1;
`;

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 24px;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  flex-wrap: wrap;
  gap: 12px;
`;

const PaginationInfo = styled.div`
  font-size: 0.82rem;
  color: #64748b;
  font-weight: 500;
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PageBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 600;
  color: #334155;
  cursor: pointer;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &:not(:disabled):hover {
    background: #f1f5f9;
  }
`;

const PageNumbers = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const NumBtn = styled.button`
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 1px solid ${(props) => (props.$active ? "var(--app-primary)" : "#cbd5e1")};
  background: ${(props) => (props.$active ? "var(--app-primary)" : "#ffffff")};
  color: ${(props) => (props.$active ? "#ffffff" : "#334155")};
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    border-color: var(--app-primary);
  }
`;
